import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DestinationsApiService } from '../../../services/destinations-api.service';
import { AudienceApiService } from '../../../services/audience-api.service';
import { NotificationService } from '../../../services/notification.service';
import { AuthService } from '../../../services/auth.service';
import { CustomSelectComponent, CustomSelectOption } from '../../shared/custom-select/custom-select.component';

interface Country {
  code: string;
  name: string;
}

export interface ActivationEditTarget {
  activationId: string;
  audienceId: string;
  destinationId: number;
  destinationTypeId: number | null;
  destinationName: string;
  externalSegmentId: string;
  channelData: Record<string, unknown> | null;
  nextExecutionDate?: string | null;
  excludeControlGroup?: boolean | null;
}

@Component({
  selector: 'app-activation-edit-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, CustomSelectComponent],
  templateUrl: './activation-edit-modal.component.html',
  styleUrl: './activation-edit-modal.component.scss'
})
export class ActivationEditModalComponent implements OnInit, OnDestroy {
  @Input() target: ActivationEditTarget | null = null;
  @Input() audienceName: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  activationParamsType: string | null = null;
  channelDataForm: FormGroup;
  isLoading = false;
  isSubmitting = false;
  countries: Country[] = [];

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private destinationsApi: DestinationsApiService,
    private audienceApi: AudienceApiService,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private authService: AuthService
  ) {
    this.channelDataForm = this.fb.group({});
  }

  get hasGlobalDataAccess(): boolean {
    return this.authService.hasGlobalDataAccess();
  }

  ngOnInit(): void {
    this.loadActivationParamsType();
    this.loadCountries();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get hasEditableFields(): boolean {
    return Object.values(this.channelDataForm.controls).some(control => control.enabled);
  }

  get rateLevelValue(): string {
    return this.channelDataForm.get('RateLevel')?.value ?? '';
  }

  get countryOptions(): CustomSelectOption[] {
    return this.countries.map(c => ({ value: c.code, label: c.name }));
  }

  get rateLevelOptions(): CustomSelectOption[] {
    return [
      { value: 'System', label: 'System' },
      { value: 'Partner', label: 'Partner' },
      { value: 'Advertiser', label: 'Advertiser' },
    ];
  }

  get ttdCurrencyOptions(): CustomSelectOption[] {
    return [{ value: 'USD', label: 'USD' }];
  }

  get ttd1pdRegionOptions(): CustomSelectOption[] {
    return [
      { value: 'US', label: 'US' },
      { value: 'EU', label: 'EU' },
    ];
  }

  get ttd1pdSegmentTypeOptions(): CustomSelectOption[] {
    return [
      { value: 'TARGETING', label: 'Targeting' },
      { value: 'CONVERSION', label: 'Conversion' },
      { value: 'TARGETING_DIRECT_IP', label: 'Targeting Direct IP' },
    ];
  }

  get ttd1pdMergeModeOptions(): CustomSelectOption[] {
    return [
      { value: '', label: 'None' },
      { value: 'ADD', label: 'Add' },
      { value: 'REPLACE', label: 'Replace' },
    ];
  }

  get zeotapAccountTypeValue(): string {
    return this.channelDataForm.get('AccountType')?.value ?? '';
  }

  get zeotapAccountTypeOptions(): CustomSelectOption[] {
    return [
      { value: 'Marketplace', label: 'Marketplace' },
      { value: 'Advertiser', label: 'Advertiser' },
    ];
  }

  onSubmit(): void {
    if (this.channelDataForm.invalid || !this.target || this.isSubmitting) return;
    this.isSubmitting = true;

    const originalChannelData = this.target.channelData ?? {};
    const { ExcludeControlGroup, ...formValue } = this.channelDataForm.getRawValue();
    // The backend overwrites ChannelData.ExternalSegmentID with the top-level
    // ExternalSegmentID, which keeps the field immutable from this form.
    const channelData: Record<string, unknown> = {
      ...originalChannelData,
      ...formValue,
      ExternalSegmentID: this.target.externalSegmentId
    };

    const payload: { ID: string; AudienceID: string; DestinationID: number; ExternalSegmentID: string; NextExecutionDate?: string; ChannelData: Record<string, unknown>; ExcludeControlGroup?: boolean } = {
      ID: this.target.activationId,
      AudienceID: this.target.audienceId,
      DestinationID: this.target.destinationId,
      ExternalSegmentID: this.target.externalSegmentId,
      ChannelData: channelData
    };
    if (this.target.nextExecutionDate) {
      payload.NextExecutionDate = this.target.nextExecutionDate.slice(0, 10);
    }
    if (this.hasGlobalDataAccess) {
      payload.ExcludeControlGroup = !!ExcludeControlGroup;
    }

    const sub = this.audienceApi.editActivationItem(payload).subscribe({
      next: () => {
        this.notificationService.notify('success',
          this.translate.instant('destinations.activation.editSuccess', { name: this.target?.destinationName ?? '' }));
        this.saved.emit();
        this.isSubmitting = false;
      },
      error: (error) => {
        console.error('[activation-edit] Edit failed', error);
        this.notificationService.notify('error',
          error?.message || this.translate.instant('destinations.activation.editError'));
        this.isSubmitting = false;
      }
    });
    this.subscriptions.add(sub);
  }

  closeModal(): void {
    this.close.emit();
  }

  private loadActivationParamsType(): void {
    this.isLoading = true;
    const sub = this.destinationsApi.getDestinationTypes().subscribe({
      next: (types) => {
        const type = (types ?? []).find(t => t.ID === this.target?.destinationTypeId) ?? null;
        this.activationParamsType = type?.ActivationParamsType ?? null;
        this.buildChannelDataForm();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('[activation-edit] Failed to load destination types', error);
        this.activationParamsType = null;
        this.buildChannelDataForm();
        this.isLoading = false;
      }
    });
    this.subscriptions.add(sub);
  }

  private channelValue(key: string, fallback: unknown = ''): unknown {
    const data = this.target?.channelData;
    if (data && key in data && data[key] !== undefined && data[key] !== null) {
      return data[key];
    }
    return fallback;
  }

  private buildChannelDataForm(): void {
    switch (this.activationParamsType) {

      case 'Pubmatic':
        this.channelDataForm = this.fb.group({
          ExternalSegmentID: [{ value: this.channelValue('ExternalSegmentID', this.target?.externalSegmentId ?? ''), disabled: true }],
          PubmaticAudienceID: [this.channelValue('PubmaticAudienceID')],
          Name: [this.channelValue('Name')],
          Description: [this.channelValue('Description')],
          Country: [this.channelValue('Country', 'IT')],
          Price: [this.channelValue('Price')],
        });
        break;

      case 'TheTradeDesk': {
        const form = this.fb.group({
          CustomAudienceName: [this.channelValue('CustomAudienceName'), Validators.required],
          CustomAudienceDesc: [this.channelValue('CustomAudienceDesc')],
          ExternalSegmentID: [{ value: this.channelValue('ExternalSegmentID', this.target?.externalSegmentId ?? ''), disabled: true }],
          ExternalSegmentParentID: [this.channelValue('ExternalSegmentParentID', null)],
          CPMRateAmount: [this.channelValue('CPMRateAmount', 0.6), Validators.required],
          CPMRateCurrencyCode: [this.channelValue('CPMRateCurrencyCode', 'USD'), Validators.required],
          PercentOfMediaCostRate: [this.channelValue('PercentOfMediaCostRate', 0.15), Validators.required],
          RateLevel: [this.channelValue('RateLevel', 'System'), Validators.required],
          AdvertiserId: [{ value: this.channelValue('AdvertiserId', null), disabled: true }],
          PartnerId: [{ value: this.channelValue('PartnerId', null), disabled: true }],
          Buyable: [this.channelValue('Buyable', true), Validators.required],
        });
        const applyRateLevel = (value: unknown) => {
          switch (value) {
            case 'Advertiser':
              form.controls['AdvertiserId'].setValidators([Validators.required]);
              form.controls['AdvertiserId'].enable();
              form.controls['PartnerId'].clearValidators();
              form.controls['PartnerId'].disable();
              break;
            case 'Partner':
              form.controls['PartnerId'].setValidators([Validators.required]);
              form.controls['PartnerId'].enable();
              form.controls['AdvertiserId'].clearValidators();
              form.controls['AdvertiserId'].disable();
              break;
            default:
              form.controls['AdvertiserId'].clearValidators();
              form.controls['AdvertiserId'].disable();
              form.controls['PartnerId'].clearValidators();
              form.controls['PartnerId'].disable();
          }
          form.controls['AdvertiserId'].updateValueAndValidity();
          form.controls['PartnerId'].updateValueAndValidity();
        };
        const rateSub = form.controls['RateLevel'].valueChanges.subscribe(value => applyRateLevel(value));
        this.subscriptions.add(rateSub);
        applyRateLevel(form.controls['RateLevel'].value);
        this.channelDataForm = form;
        break;
      }

      case 'TheTradeDesk1pd':
        this.channelDataForm = this.fb.group({
          CustomAudienceName: [this.channelValue('CustomAudienceName'), Validators.required],
          Region: [this.channelValue('Region'), Validators.required],
          SegmentType: [this.channelValue('SegmentType'), Validators.required],
          MergeMode: [this.channelValue('MergeMode', null)],
          AdvertiserID: [this.channelValue('AdvertiserID', null)],
        });
        break;

      case 'TheTradeDeskOfflineConversion':
        this.channelDataForm = this.fb.group({
          TrackingTagName: [this.channelValue('TrackingTagName'), Validators.required],
          ExternalSegmentID: [{ value: this.channelValue('ExternalSegmentID', this.target?.externalSegmentId ?? ''), disabled: true }],
        });
        break;

      case 'AudienceRealtime':
        this.channelDataForm = this.fb.group({
          ExternalAudienceName: [this.channelValue('ExternalAudienceName')],
          Description: [this.channelValue('Description')],
          OwnerID: [this.channelValue('OwnerID'), Validators.required],
          TTL: [this.channelValue('TTL', 91), Validators.required],
        });
        break;

      case 'Zeotap': {
        const zeotapAccountType = (this.channelValue('AccountType', 'Marketplace') as string) || 'Marketplace';
        const isAdvertiser = zeotapAccountType === 'Advertiser';
        const rawCountries = this.channelValue('Countries', ['IT']);
        const countriesValue = Array.isArray(rawCountries)
          ? rawCountries
          : String(rawCountries).split(',').map(s => s.trim()).filter(Boolean);
        this.channelDataForm = this.fb.group({
          AccountType: [{ value: zeotapAccountType, disabled: true }],
          ExternalSegmentID: [{ value: this.channelValue('ExternalSegmentID', this.target?.externalSegmentId ?? ''), disabled: true }],
          SegmentName: [{ value: this.channelValue('SegmentName'), disabled: !isAdvertiser }, isAdvertiser ? Validators.required : []],
          Description: [{ value: this.channelValue('Description'), disabled: !isAdvertiser }],
          Price: [{ value: this.channelValue('Price', null), disabled: !isAdvertiser }, isAdvertiser ? [Validators.required, Validators.min(0)] : []],
          Countries: [{ value: countriesValue, disabled: !isAdvertiser }, isAdvertiser ? Validators.required : []],
          ZeotapDestinationID: [{ value: this.channelValue('ZeotapDestinationID'), disabled: !isAdvertiser }, isAdvertiser ? Validators.required : []],
        });
        break;
      }

      case 'ExternalSegment':
      case 'AdformAPI':
      case 'Generic':
      case 'generic':
      default:
        this.channelDataForm = this.fb.group({
          ExternalSegmentID: [{ value: this.channelValue('ExternalSegmentID', this.target?.externalSegmentId ?? ''), disabled: true }],
        });
    }

    if (this.hasGlobalDataAccess) {
      this.channelDataForm.addControl('ExcludeControlGroup', this.fb.control(!!this.target?.excludeControlGroup));
    }
  }

  private loadCountries(): void {
    const sub = this.audienceApi.getCountries().subscribe({
      next: (countries) => { this.countries = countries; },
      error: (err) => { console.error('[activation-edit] Failed to load countries', err); }
    });
    this.subscriptions.add(sub);
  }

}
